import { AlertFocusIdentity, AlertSeverity } from '@aloft/toolkit-shared';
import { colors } from '@aloft/toolkit-sui';

import toast from 'react-hot-toast';

import { AlertSeverityIcon } from '../../modules/alerts/AlertSeverityIcon.tsx';
import { ALERT_SEVERITY_ICON_PROPS } from '../../modules/alerts/alertSeverityIconProps.ts';
import { isAiIconAlert } from '../../modules/alerts/definitions/comparable/inconsistentSubjectRating.ts';
import { openAlerts } from '../../utils/openAlerts.ts';
import { RenderToast } from './RenderToast.tsx';
import {
  COMPARABLES_ALERTS_TOAST_CLASS_NAME,
  TOAST_CLASS_NAME,
  TOAST_DURATION_IN_MS,
  TOAST_PERSISTENT_DURATION_IN_MS,
} from './constants.ts';
import {
  dismissAlertToasts,
  dismissAllToasts,
  registerAlertToast,
  suppressAlertToast,
} from './dismissAllToasts.ts';
import { MakeToast, ToastType } from './types.ts';

const toastTypeToIconProps = {
  warning: ALERT_SEVERITY_ICON_PROPS.warning,
  info: {
    icon: 'information',
    color: 'surfaceAccent6',
  },
  success: {
    icon: 'check-circle',
    color: 'success',
  },
  error: ALERT_SEVERITY_ICON_PROPS.error,
};

const toastTypeToStyleProps: {
  [key in ToastType]: React.CSSProperties;
} = {
  warning: {
    backgroundColor: colors.alertWarningLight,
    borderColor: colors.alertWarning,
  },
  info: {
    backgroundColor: colors.surface3,
    borderColor: colors.surfaceAccent6,
  },
  success: {
    backgroundColor: colors.alertSuccessLight,
    borderColor: colors.success,
  },
  error: {
    backgroundColor: colors.alertErrorLight,
    borderColor: colors.alertError,
  },
};

const makeReactHotToast: MakeToast = ({
  id,
  type,
  title,
  body,
  action,
  compType,
  onClick,
  openAlertsOnClick,
  singleSlice = true,
  durationInMs = TOAST_DURATION_IN_MS,
  position = 'bottom-right',
  comparablesAlerts = false,
  alertType,
  alertPropertyID,
  alertTargetCellKey,
  alertKey,
}) => {
  const iconProps = toastTypeToIconProps[type];
  const styleProps = toastTypeToStyleProps[type];
  const isClickable = Boolean(onClick ?? openAlertsOnClick);
  const isAlertToast = comparablesAlerts || openAlertsOnClick;
  const isAiAlertToast = Boolean(alertType && isAiIconAlert(alertType));
  const duration = isAlertToast ? TOAST_PERSISTENT_DURATION_IN_MS : durationInMs;
  const toastPosition = isAlertToast ? 'top-right' : position;

  toast(
    () => (
      <RenderToast
        title={title}
        body={body}
        compType={compType}
        action={action}
        alertType={isAlertToast ? alertType : undefined}
        alertPropertyID={isAlertToast ? alertPropertyID : undefined}
        alertTargetCellKey={isAlertToast ? alertTargetCellKey : undefined}
        alertSeverity={isAlertToast ? type : undefined}
        isClickable={isClickable}
        onClick={
          isClickable
            ? () => {
                onClick?.();
                if (openAlertsOnClick) {
                  dismissAlertToasts();
                  const focusedAlert: AlertFocusIdentity | undefined = alertType
                    ? {
                        type: alertType,
                        propertyID: alertPropertyID ?? null,
                        targetCellKey: alertTargetCellKey ?? null,
                      }
                    : undefined;
                  openAlerts(focusedAlert);
                } else {
                  dismissAllToasts();
                }
              }
            : undefined
        }
      />
    ),
    {
      id: singleSlice ? id : undefined,
      duration,
      position: toastPosition,
      style: {
        border: '1px solid',
        maxWidth: '600px',
        ...(isAlertToast ? {} : styleProps),
        ...(isAiAlertToast
          ? { backgroundColor: colors.alertAiLight, borderColor: colors.alertAi }
          : {}),
      },
      className: [
        TOAST_CLASS_NAME,
        isAlertToast ? COMPARABLES_ALERTS_TOAST_CLASS_NAME : undefined,
        isAlertToast ? `comparables-alerts-toast--${type}` : undefined,
        isAiAlertToast ? 'comparables-alerts-toast--ai' : undefined,
      ]
        .filter(Boolean)
        .join(' '),
      icon: (
        <AlertSeverityIcon
          severity={type as AlertSeverity}
          variant={isAiAlertToast ? 'ai' : 'severity'}
        />
      ),
      ...(isAlertToast && alertType && alertKey
        ? {
            onDismiss: () => {
              suppressAlertToast(alertKey);
            },
          }
        : {}),
    } as Parameters<typeof toast>[1],
  );

  if (isAlertToast && alertKey) {
    registerAlertToast(alertKey);
  }
};

export const useToaster = () => makeReactHotToast;
